import React, { useEffect, useState } from "react";
import { getMediaSettings, setMediaSettings } from "../../api/apiServiceFetch";
import { setFileBaseUrl } from "../../config/environment";

// Значения должны совпадать с белым списком на бэкенде (ConfigRepo).
const TARGETS = [
	{ value: "both", label: "Облако и диск" },
	{ value: "s3", label: "Только облако" },
	{ value: "disk", label: "Только диск" },
];

const DEFAULTS = {
	writeTarget: "both",
	deleteTarget: "both",
	signedUrlTtlMinutes: 15,
	baseUrl: "",
};

/**
 * Управление хранением медиа. Раньше это жило в конфиге сервера и менялось
 * только выкладкой; теперь — в базе, и действует в течение минуты (столько
 * живёт кэш настроек на бэкенде).
 */
const MediaSettingsCard = () => {
	const [form, setForm] = useState(DEFAULTS);
	const [saved, setSaved] = useState(DEFAULTS);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [status, setStatus] = useState(null); // { ok: bool, text: string }

	useEffect(() => {
		(async () => {
			try {
				const data = await getMediaSettings();
				const current = {
					writeTarget: data?.writeTarget || DEFAULTS.writeTarget,
					deleteTarget: data?.deleteTarget || DEFAULTS.deleteTarget,
					signedUrlTtlMinutes:
						Number(data?.signedUrlTtlMinutes) || DEFAULTS.signedUrlTtlMinutes,
					baseUrl: data?.baseUrl || "",
				};
				setForm(current);
				setSaved(current);
				setFileBaseUrl(current.baseUrl);
			} catch (error) {
				console.error("Load media settings error:", error);
				setStatus({ ok: false, text: "Не удалось загрузить настройки" });
			} finally {
				setIsLoading(false);
			}
		})();
	}, []);

	const update = (patch) => {
		setForm((prev) => ({ ...prev, ...patch }));
		setStatus(null);
	};

	const isDirty =
		form.writeTarget !== saved.writeTarget ||
		form.deleteTarget !== saved.deleteTarget ||
		Number(form.signedUrlTtlMinutes) !== Number(saved.signedUrlTtlMinutes) ||
		form.baseUrl !== saved.baseUrl;

	const handleSave = async () => {
		setIsSaving(true);
		setStatus(null);
		try {
			const data = await setMediaSettings({
				writeTarget: form.writeTarget,
				deleteTarget: form.deleteTarget,
				signedUrlTtlMinutes: Number(form.signedUrlTtlMinutes),
				baseUrl: form.baseUrl.trim(),
			});

			const current = {
				writeTarget: data?.writeTarget || form.writeTarget,
				deleteTarget: data?.deleteTarget || form.deleteTarget,
				signedUrlTtlMinutes:
					Number(data?.signedUrlTtlMinutes) || Number(form.signedUrlTtlMinutes),
				baseUrl: data?.baseUrl || form.baseUrl.trim(),
			};

			setForm(current);
			setSaved(current);
			setFileBaseUrl(current.baseUrl);
			setStatus({ ok: true, text: "Сохранено. Применится в течение минуты." });
		} catch (error) {
			console.error("Save media settings error:", error);
			setStatus({
				ok: false,
				text: "Ошибка сохранения. Проверьте адрес и срок ссылки.",
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<section className="settings-page__card">
			<h3>Хранение файлов</h3>
			<p className="settings-page__hint">
				Куда сохраняются фотографии, селфи, видео и вложения чата и откуда они
				удаляются. Безопасный порядок перехода: сначала «Облако и диск»,
				убедиться, что всё открывается, и только потом «Только облако».
				Адреса хранилища и ключи доступа остаются в настройках сервера.
			</p>

			<div className="settings-page__row">
				<label className="settings-page__label">Сохранять</label>
				<select
					value={form.writeTarget}
					disabled={isLoading || isSaving}
					onChange={(e) => update({ writeTarget: e.target.value })}
				>
					{TARGETS.map((t) => (
						<option key={t.value} value={t.value}>
							{t.label}
						</option>
					))}
				</select>
			</div>

			<div className="settings-page__row">
				<label className="settings-page__label">Удалять</label>
				<select
					value={form.deleteTarget}
					disabled={isLoading || isSaving}
					onChange={(e) => update({ deleteTarget: e.target.value })}
				>
					{TARGETS.map((t) => (
						<option key={t.value} value={t.value}>
							{t.label}
						</option>
					))}
				</select>
			</div>

			<div className="settings-page__row">
				<label className="settings-page__label">Ссылка на файл чата живёт</label>
				<input
					type="number"
					min="1"
					max="1440"
					value={form.signedUrlTtlMinutes}
					disabled={isLoading || isSaving}
					onChange={(e) => update({ signedUrlTtlMinutes: e.target.value })}
				/>
				<span className="settings-page__hint settings-page__hint--inline">
					минут (1–1440)
				</span>
			</div>

			<div className="settings-page__row">
				<label className="settings-page__label">Адрес для фото и видео</label>
				<input
					type="text"
					value={form.baseUrl}
					placeholder="https://s3.regru.cloud/edia-cdn/Resources/"
					disabled={isLoading || isSaving}
					onChange={(e) => update({ baseUrl: e.target.value })}
				/>
			</div>

			<p className="settings-page__hint">
				Приложение и сайт берут этот адрес с сервера. Установленные версии
				приложения узнают его только после обновления — пока их много,
				держите режим «Облако и диск».
			</p>

			<div className="settings-page__row">
				<button
					type="button"
					disabled={isLoading || isSaving || !isDirty}
					onClick={handleSave}
				>
					{isSaving ? "Сохранение..." : "Сохранить"}
				</button>
			</div>

			{status && (
				<p
					className={`settings-page__status ${
						status.ok ? "is-ok" : "is-error"
					}`}
				>
					{status.text}
				</p>
			)}
		</section>
	);
};

export default MediaSettingsCard;
